import React, { useContext, useEffect } from "react";
import { useImmer } from "use-immer";
import { successMessage, errorMessage } from "../../../utils";
import { useParams, useLocation } from "react-router-dom";
import { AppContext } from "../../../store";
import { usePermission } from "../../../shared";
import {
  getTandaDetailsApi,
  updateTandaConnectionApi,
  disconnectTandaApi,
  getTandaVerifiedApi,
  getTandaDataStreamsApi,
  selectTandaDataStreamApi,
} from "../api/tandaApi";
import { getStoreId } from "../../../utils/helper";
import { SITE_URL } from "../../../utils/apiConfig"; // Import SITE_URL from apiConfig

export const useTandaDetails = () => {
  const { id } = useParams();
  const storeId = getStoreId();
  const { pathname } = useLocation();
  const location = useLocation();
  const isStoreAdmin = pathname.includes("store-admin");
  const isEditable = usePermission("integration-modify");
  const { appState } = useContext(AppContext);

  const [state, setState] = useImmer({
    isBusy: true,
    isDetails: false,
    isSaveButton: false,
    isOpen: false,
    isOpenConnect: false,
    isModalType: "",
    external_store_id: "",
    errMsg: "",
    details: {},
    inputData: {
      application_id: "",
      client_secret: "",
    },
    isDataStreamOpen: false,
    isDataStreamLoading: false,
    dataStreams: [],
    dataStreamInput: {
      create_new: false,
      data_stream_id: "",
      name: "",
    },
  });

  const storeIdToUse = id || storeId;
  let redirectURL = isStoreAdmin
    ? `${SITE_URL}store-admin/tanda?id=${storeIdToUse}`
    : `${SITE_URL}tanda?id=${storeIdToUse}`; // Use SITE_URL from apiConfig

  // Fetch Tanda details
  const getTandaDetails = async () => {
    try {
      setState((draft) => {
        draft.isBusy = true;
      });

      const storeIdToUse = id || storeId;
      const response = await getTandaDetailsApi(storeIdToUse);

      if (response.success) {
        setState((draft) => {
          draft.details = response.data;
          // Set input data for fields if available
          if (response.data.application_id) {
            draft.inputData.application_id = response.data.application_id;
          }
          if (response.data.client_secret) {
            draft.inputData.client_secret = response.data.client_secret;
          }
          draft.isDetails = true;
          draft.isBusy = false;
        });
      } else {
        setState((draft) => {
          draft.errMsg = response.message || "Failed to fetch Tanda details";
          draft.isDetails = false;
          draft.isBusy = false;
        });
      }
    } catch (error) {
      console.error("Error fetching Tanda details:", error);
      setState((draft) => {
        draft.errMsg = error.response?.data?.message || "Something went wrong";
        draft.isDetails = false;
        draft.isBusy = false;
      });
    }
  };

  // Handle connection modal
  const handleModal = (type, isOpen, data = null) => {
    if (type === "disconnect") {
      setState((draft) => {
        draft.isOpenConnect = isOpen;
      });
    } else if (type === "connect") {
      setState((draft) => {
        draft.isOpen = isOpen;
        draft.isModalType = type;
      });
    } else if (type === "dataStream") {
      setState(draft => {
        draft.isDataStreamOpen = isOpen;
      });
      if (isOpen) {
        getDataStreams();
      }
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Handle nested input data for connect dialog
    if (["application_id", "client_secret", "redirect_uri"].includes(name)) {
      setState((draft) => {
        draft.inputData[name] = value;
      });
    } else {
      setState((draft) => {
        draft[name] = value;
      });
    }
  };

  // Initial data fetch
  useEffect(() => {
    getTandaDetails();
  }, [id, storeId]);

  const onChange = (e) => {
    setState((draft) => {
      draft.inputData[e.target.name] = e.target.value;
    });
  };

  const handleStoreConnect = async (store, status) => {
    try {
      setState((draft) => {
        draft.isSaveButton = true;
      });

      // Get the store ID from the URL params or the current store ID
      const storeIdToUse = id || storeId;

      const payload = {
        ...state.inputData,
        redirect_uri: redirectURL, // Use SITE_URL from apiConfig
        store_id: storeIdToUse,
      };

      const response = await updateTandaConnectionApi(payload);

      if (response.success) {
        //response.data is the url to redirect to
        if (response.data) {
          window.open(response.data, "_blank");
        }
        successMessage(
          response.message || "Connection status updated successfully"
        );
        handleModal("connect", false);
        getTandaDetails(); // Refresh details after connection
      } else {
        errorMessage(response.message || "Failed to update connection status");
      }

      setState((draft) => {
        draft.isSaveButton = false;
      });
    } catch (error) {
      console.error("Error connecting to Tanda:", error);
      errorMessage(error.response?.data?.message || "Something went wrong");
      setState((draft) => {
        draft.isSaveButton = false;
      });
    }
  };

  // Add this function for disconnecting Tanda
  const handleStoreDisconnect = async () => {
    try {
      setState((draft) => {
        draft.isSaveButton = true;
      });

      const storeIdToUse = id || storeId;
      const payload = {
        store_id: storeIdToUse,
      };

      const response = await disconnectTandaApi(payload);

      if (response.success) {
        successMessage(response.message || "Tanda successfully disconnected");
        getTandaDetails(); // Refresh details after disconnection
        handleModal("disconnect", false);
      } else {
        errorMessage(response.message || "Failed to disconnect Tanda");
      }

      setState((draft) => {
        draft.isSaveButton = false;
        draft.isOpenConnect = false;
      });
    } catch (error) {
      errorMessage(error.response?.data?.message || "Something went wrong");
      setState((draft) => {
        draft.isSaveButton = false;
      });
    }
  };

  // Check URL for Tanda auth parameters
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const code = queryParams.get("code");
    const id = queryParams.get("id");

    if (code && id) {
      verifyTandaAuth(code, id);

      // Clean the URL after processing the parameters
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    }
  }, [location.search]);

  // Handle Tanda verification
  const verifyTandaAuth = async (code, id) => {
    try {
      const payload = { code, store_id: id };
      const response = await getTandaVerifiedApi(payload);

      if (response.success) {
        successMessage(response.message || "Tanda authentication successful");
        getTandaDetails(); // Refresh details after verification
        // Prompt for a data stream right away, the store has none yet
        handleModal("dataStream", true);
      } else {
        errorMessage(response.message || "Tanda authentication failed");
      }
    } catch (error) {
      errorMessage(
        error.response?.data?.message || "Authentication verification failed"
      );
    }
  };


  // Fetch the account's existing Tanda data streams
  const getDataStreams = async () => {
    try {
      setState(draft => {
        draft.isDataStreamLoading = true;
      });

      const response = await getTandaDataStreamsApi({ store_id: storeIdToUse });

      if (response.success) {
        const { data_streams, selected_data_stream_id } = response.data || {};
        const streams = data_streams || [];

        setState(draft => {
          draft.dataStreams = streams;
          // Preselect the stream already in use, else the first available one
          draft.dataStreamInput = {
            create_new: !streams.length,
            data_stream_id: selected_data_stream_id ?? streams[0]?.id ?? "",
            name: "",
          };
          draft.isDataStreamLoading = false;
        });
      } else {
        errorMessage(response.message || "Failed to fetch data streams");
        setState(draft => {
          draft.isDataStreamLoading = false;
        });
      }
    } catch (error) {
      errorMessage(error.response?.data?.message || "Something went wrong");
      setState(draft => {
        draft.isDataStreamLoading = false;
      });
    }
  };

  const onChangeDataStream = (name, value) => {
    setState(draft => {
      draft.dataStreamInput[name] = value;
    });
  };

  // Store the chosen data stream, or create a new one with the given name
  const handleDataStreamSelect = async () => {
    try {
      setState(draft => {
        draft.isSaveButton = true;
      });

      const { create_new, data_stream_id, name } = state.dataStreamInput;
      const payload = {
        store_id: storeIdToUse,
        create_new,
        data_stream_id: create_new ? null : data_stream_id,
        name: create_new ? name : null,
      };

      const response = await selectTandaDataStreamApi(payload);

      if (response.success) {
        successMessage(response.message || "Data stream selected successfully");
        handleModal("dataStream", false);
        getTandaDetails(); // Refresh details to show the new data stream
      } else {
        errorMessage(response.message || "Failed to select data stream");
      }

      setState(draft => {
        draft.isSaveButton = false;
      });
    } catch (error) {
      errorMessage(error.response?.data?.message || "Something went wrong");
      setState(draft => {
        draft.isSaveButton = false;
      });
    }
  };

  return {
    state,
    redirectURL,
    storeIdToUse,
    isStoreAdmin,
    isEditable,
    handleModal,
    handleInputChange,
    handleStoreConnect,
    onChange,
    handleStoreDisconnect,
    onChangeDataStream,
    handleDataStreamSelect,
  };
};
